// App/Scripts/BasemapSwitcher.js
import { map } from './Map';
import Group from 'ol/layer/Group';
import TileLayer from 'ol/layer/Tile';
import XYZ from 'ol/source/XYZ';
import TileArcGISRest from 'ol/source/TileArcGISRest';
import WMTS, { optionsFromCapabilities } from 'ol/source/WMTS';
import WMTSCapabilities from 'ol/format/WMTSCapabilities';
import TileWMS from 'ol/source/TileWMS';
import { unByKey } from 'ol/Observable';
import Collection from 'ol/Collection';
import TileGrid from 'ol/tilegrid/TileGrid';
import { getCenter, getTopLeft, containsCoordinate } from 'ol/extent';
import { ARCGIS_SERVICES, GEOMOLG_RESOLUTIONS, GAZA_IMAGERY_SERVICES, REGION_EXTENTS, MAP_CONFIG, GEOSERVER_WMTS_URL, MAP_PROJECTION_CODE } from './Config';

// Zoom limit = 1:25000 approx resolution 7.0
const RESTRICT_RES_THRESHOLD = 7.0;
let lastActiveAerialId = null; // Smart Restore History
let isYearWidgetManuallyClosed = false;
let isSwipeWidgetManuallyClosed = false;
let isRestricted = false; // Flag for restricted mode (zoomed out)
let cachedRegion = null;
let isGeomolgOffline = false;
const GEOMOLG_HOST = 'orthophotos.geomolg.ps';
const BACKUP_IMAGERY_ID = 'backup_arcgis_2023';
const BACKUP_WB_2023_URL = 'https://utility.arcgis.com/usrsvcs/servers/ce6ba532032a4a348ba482458084e70f/rest/services/AerialPhoto/WestBank_2023_V02/MapServer';
const GOOGLE_HYBRID_ID = 'google_hybrid';

// Client-side tile failure tracking (per user, no backend health check)
const GEOMOLG_FAILURE_THRESHOLD = 3;
let geomolgConsecutiveFailures = 0;
let geomolgFallbackPending = false;

// DOM Caching for Performance
let bmPopover = null;
let btn2023 = null;
let btnYears = null;
let btnSwipe = null;
let cachedBaseId = 'world_sat'; // Initial state
let isYearWidgetVisible = false;
let isSwipeWidgetVisible = false;

/**
 * Enforces or releases zoom constraints.
 * When enabled, users cannot zoom out past the 1:25,000 threshold.
 */
function enforceAerialConstraints(enable) {
  const view = map.getView();
  if (!view) return;

  if (enable) {
    // Calculate the zoom level corresponding to the threshold resolution
    // We add a small buffer to ensure they don't accidentally hit the restricted zone
    const restrictedZoom = view.getZoomForResolution(RESTRICT_RES_THRESHOLD);
    // Ensure the current zoom is valid before locking
    if (view.getResolution() > RESTRICT_RES_THRESHOLD) {
      // Subtracting from resolution pushes the zoom inward, staying safely past the 7.0 restricted threshold.
      view.setResolution(RESTRICT_RES_THRESHOLD - 0.01); // Snap to valid
    }
    view.setMinZoom(restrictedZoom);
  } else {
    // Release lock
    view.setMinZoom(MAP_CONFIG.minZoom || 0);
  }
}


function updateBasemapUI(isGaza) {
  // 1. Aerial 2023 Button
  if (btn2023) {
    btn2023.style.display = isGaza ? 'none' : '';
  }

  // 2. Years Group Button Text
  if (btnYears) {
    btnYears.textContent = isGaza ? 'صور اقمار صناعية (قطاع غزة)' : 'صور جوية (الضفة الغربية)';
  }

  // 3. Swipe Button Text
  if (btnSwipe) {
    btnSwipe.textContent = isGaza ? 'مقارنة (قطاع غزة)' : 'مقارنة (الضفة الغربية)';
  }
}

function checkMapState() {
  const view = map.getView();
  const center = view.getCenter();
  const currentRegion = containsCoordinate(REGION_EXTENTS.GAZA, center) ? 'Gaza' : 'WB';

  // Aggressive Early Exit (O(1)) - Fastest for panning
  if (cachedBaseId === 'world_sat' && !isRestricted && cachedRegion === currentRegion) {
    updateImageryWidgetVisibility();
    updateSwipeWidgetVisibility();
    return;
  }

  const res = view.getResolution();
  const isNowRestricted = res > RESTRICT_RES_THRESHOLD;

  // 1. Zoom Restriction Logic
  if (isNowRestricted) {
    if (!isRestricted) {
      isRestricted = true;
      const currentId = cachedBaseId; // Use cached ID for speed
      const isAnyActive = isAnyImageryActive() || (imageryIdsWB.has(currentId) || imageryIdsGaza.has(currentId)) || currentId === 'swipe_compare';
      if (isAnyActive && currentId !== 'world_sat') {
        lastActiveAerialId = currentId;
        setBaseMap('world_sat');
      }
      updateImageryWidgetVisibility();
      updateSwipeWidgetVisibility();
    }
    return;
  }

  // 2. Not restricted: Handle Region Change with Caching
  const wasRestricted = isRestricted;
  isRestricted = false;

  if (cachedRegion !== currentRegion) {
    cachedRegion = currentRegion;
    const isGaza = currentRegion === 'Gaza';
    CURRENT_IMAGERY_LIST = isGaza ? IMAGERY_GAZA : IMAGERY_WB;
    cachedImagerySet = new Set(CURRENT_IMAGERY_LIST.map(x => x.id)); // High-perf rebuild

    updateBasemapUI(isGaza); // Only update DOM UI on regional change
    updateWidgetRange();
    populateSwipeSelects();

    const currentId = cachedBaseId;
    if ((imageryIdsWB.has(currentId) || imageryIdsGaza.has(currentId)) && !isRestricted) {
      setBaseMap(CURRENT_IMAGERY_LIST[0].id);
    }
    if (currentId === 'swipe_compare') {
      updateSwipeLayers();
    }
  }

  // 3. AUTO-SWITCH / SMART RESTORE LOGIC
  if (wasRestricted && !isRestricted) {
    isYearWidgetManuallyClosed = false;
    isSwipeWidgetManuallyClosed = false;

    if (cachedBaseId === 'world_sat') {
      if (lastActiveAerialId) {
        const isSwipe = lastActiveAerialId === 'swipe_compare';
        const isInList = CURRENT_IMAGERY_LIST.some(x => x.id === lastActiveAerialId);
        if (isSwipe || isInList) {
          setBaseMap(lastActiveAerialId);
        } else {
          setBaseMap(CURRENT_IMAGERY_LIST[0].id);
        }
      } else {
        setBaseMap(CURRENT_IMAGERY_LIST[0].id);
      }
    }
    updateImageryWidgetVisibility();
    updateSwipeWidgetVisibility();
  }
}


function setBaseMap(id, isSystemFallback = false) {
  // Cleanup old active basemap before switching
  const oldActive = baseGroup.getLayers().getArray().find(l => l.getVisible());
  if (oldActive && oldActive.get('id') !== id) {
    // 1) Remove old monitoring handlers
    const cleanup = oldActive.get('_cleanup');
    if (cleanup) { cleanup(); oldActive.set('_cleanup', null); }

    // 2) Clear tile sources for memory safety
    if (oldActive instanceof TileLayer) {
      const src = oldActive.getSource(); if (src && src.clear) src.clear();
    } else if (oldActive instanceof Group && oldActive.get('swipeTop')) {
      const top = oldActive.get('swipeTop'); const bottom = oldActive.get('swipeBottom');
      [top, bottom].forEach(layer => {
        if (layer) {
          const src = layer.getSource(); if (src && src.clear) src.clear();
          const c = layer.get('_cleanup'); if (c) { c(); layer.set('_cleanup', null); }
        }
      });
    }
  }

  if (id !== 'swipe_compare') detachSwipe();

  isYearWidgetManuallyClosed = false;
  isSwipeWidgetManuallyClosed = false;

  const targetIsAerial = imageryIdsWB.has(id) || imageryIdsGaza.has(id) || id === 'swipe_compare';
  if (isRestricted && targetIsAerial) {
    if (window.showErrorModal) window.showErrorModal('Zoom in past 1:25000 to view aerial imagery.');
    else alert('Zoom in past 1:25000 to view aerial imagery.');
    return;
  }

  baseGroup.getLayers().forEach((l) => {
    const isVisible = l.get('id') === id;
    if (isVisible && l.get('isLazy')) {
      const factory = l.get('bmFactory');
      if (factory) {
        const real = factory();
        l.setSource(real.getSource());
        if (real.getExtent()) l.setExtent(real.getExtent());
        if (real.get('originalUrl')) l.set('originalUrl', real.get('originalUrl'));
        if (real.get('_cleanup')) l.set('_cleanup', real.get('_cleanup'));
        l.set('isLazy', false);
      }
    }
    l.setVisible(isVisible);
    if (l instanceof TileLayer) l.setPreload(0);
  });

  if (id === 'swipe_compare') {
    const parcelLayer = map.getLayers().getArray().find(l => l?.get?.('title') === 'Parcels');
    if (parcelLayer) parcelLayer.setVisible(false);
  }

  // Only save to localStorage if it's a voluntary user action, 
  // NOT an automatic system fallback. This prevents getting stuck on the backup layer.
  if (!isSystemFallback) {
    try {
      localStorage.setItem('geopst_basemap', id);
    } catch (e) {
      console.warn('Failed to save basemap preference to localStorage:', e);
    }
  }

  cachedBaseId = id; // Update Global State Cache

  const cfg = BASEMAPS.find((x) => x.id === id);
  const thumb = document.getElementById('bm-thumb-img');
  if (thumb && cfg) thumb.src = cfg.thumb;

  updateImageryWidgetVisibility();
  syncImageryWidgetToActive();
  updateSwipeWidgetVisibility();
  if (id === 'swipe_compare') updateSwipeLayers();
  bindSwipeIfActive();
}

// Ensure init calls consistent state


const geomolgTileGrid = new TileGrid({
  origin: [-4839400, 6616800],
  resolutions: GEOMOLG_RESOLUTIONS,
  tileSize: 256,
  extent: [70000, 60000, 240000, 240000], // Approximate local extent
});

// ? Restricted Grid to force upscaling (stops at Level 12)
const geomolgTileGridMax12 = new TileGrid({
  origin: [-4839400, 6616800],
  resolutions: GEOMOLG_RESOLUTIONS.slice(0, 13), // 0 to 12
  tileSize: 256,
  extent: [70000, 60000, 240000, 240000],
});

/* =============== Basemap ID sets =============== */

// West Bank Imagery (Existing)
let IMAGERY_WB = [
  { year: 2024, id: 'ortho2024' },
  { year: 2023, id: 'ortho2023' },
  { year: 2022, id: 'ortho2022' },
  { year: 2021, id: 'ortho2021' },
  { year: 2020, id: 'ortho2020' },
  { year: 2019, id: 'ortho2019' },
  { year: 2018, id: 'ortho2018' },
  { year: 2016, id: 'ortho2016' },
  { year: 2015, id: 'ortho2015' },
  { year: 2014, id: 'ortho2014' },
  { year: 2013, id: 'ortho2013' },
  { year: 2012, id: 'ortho2012' },
  { year: 2011, id: 'ortho2011' },
  { year: 2010, id: 'ortho2010' },
  { year: 2009, id: 'ortho2009' },
  { year: 2008, id: 'ortho2008' },
  { year: 2006, id: 'ortho2006' },
  { year: 2005, id: 'ortho2005' },
  { year: 2004, id: 'ortho2004' },
  { year: 2003, id: 'ortho2003' },
  { year: 2002, id: 'ortho2002' },
  { year: 2001, id: 'ortho2001' },
  { year: 2000, id: 'ortho2000' },
  { year: 1999, id: 'ortho1999' },
  { year: 1997, id: 'ortho1997' },
  { year: 1970, id: 'ortho_geoserver_1970' },
];

// Gaza Imagery (New)
const IMAGERY_GAZA = [
  { year: 2025, id: 'gaza2025' },
  { year: 2024, id: 'gaza2024' },
  { year: 2022, id: 'gaza2022' },
  { year: 2021, id: 'gaza2021' },
  { year: 2019, id: 'gaza201811' }, // Intentionally maps to late 2018 service from Config.js
  { year: 2018, id: 'gaza2018' },
  { year: 2015, id: 'gaza201408' },
  { year: 2014, id: 'gaza201407' },
  { year: 2005, id: 'gaza2005' },
  { year: 2004, id: 'gaza2004' },
];

const imageryIdsWB = new Set(IMAGERY_WB.map(x => x.id));
const imageryIdsGaza = new Set(IMAGERY_GAZA.map(x => x.id));

/* =============== Helper functions =============== */

function isActiveGeomolgBasemap() {
  const currentId = getCurrentBaseId();
  return imageryIdsWB.has(currentId) || imageryIdsGaza.has(currentId);
}

function reportGeomolgTileSuccess() {
  if (isGeomolgOffline) return;
  geomolgConsecutiveFailures = 0;
}

function reportGeomolgTileFailure() {
  if (isGeomolgOffline || !isActiveGeomolgBasemap()) return;
  geomolgConsecutiveFailures++;
  if (geomolgConsecutiveFailures >= GEOMOLG_FAILURE_THRESHOLD) {
    scheduleGeomolgFallback();
  }
}

function scheduleGeomolgFallback() {
  if (isGeomolgOffline || geomolgFallbackPending) return;
  geomolgFallbackPending = true;
  setTimeout(() => {
    geomolgFallbackPending = false;
    if (!isGeomolgOffline && geomolgConsecutiveFailures >= GEOMOLG_FAILURE_THRESHOLD) {
      activateGeomolgFallback();
    }
  }, 100);
}

function attachGeomolgTileWatch(source) {
  source.on('tileloadend', reportGeomolgTileSuccess);
  source.on('tileloaderror', reportGeomolgTileFailure);
}

/**
 * Circuit breaker only. Do not wrap onload/onerror here — OpenLayers
 * already tracks tile load, and extra hooks make aerial tiles feel laggy.
 */
function geomolgTileLoadFunction(tile, src) {
  if (isGeomolgOffline === true && src.includes(GEOMOLG_HOST)) {
    tile.setState(3);
    return;
  }
  tile.getImage().src = src;
}

function bmXYZ(url, extra = {}) {
  const isGeomolg = url.includes(GEOMOLG_HOST);
  const source = new XYZ({
    url,
    crossOrigin: 'anonymous',
    tileLoadFunction: isGeomolg ? geomolgTileLoadFunction : undefined,
    ...extra
  });
  if (isGeomolg) attachGeomolgTileWatch(source);
  return new TileLayer({
    source,
    visible: false,
    preload: 0,
    useInterimTilesOnError: false,
    properties: { isBase: true },
    transition: 0,
  });
}
function bmArcGISTiles(url, extent = REGION_EXTENTS.WEST_BANK) {
  const isGeomolg = url.includes(GEOMOLG_HOST);
  const src = new XYZ({
    url: `${url}/tile/{z}/{y}/{x}`,
    crossOrigin: 'anonymous',
    projection: 'EPSG:28191',
    tileGrid: geomolgTileGridMax12,
    wrapX: false,
    transition: 0,
    tileLoadFunction: isGeomolg ? geomolgTileLoadFunction : undefined,
  });
  if (isGeomolg) attachGeomolgTileWatch(src);
  return new TileLayer({
    source: src,
    extent: extent,
    visible: false,
    preload: 0,
    useInterimTilesOnError: true,
    properties: { isBase: true, originalUrl: url },
    transition: 0,
  });
}
function bmArcGIS(url, isGaza = false) {
  const src = new TileArcGISRest({ url, crossOrigin: 'anonymous', transition: 0 });
  return new TileLayer({
    source: src,
    visible: false,
    useInterimTilesOnError: true,
    properties: { isBase: true, isGaza },
    transition: 0,
  });
}
function bmTileWMS({ url, layers, format = 'image/jpeg' }) {
  const src = new TileWMS({
    url,
    params: { LAYERS: layers, FORMAT: format, TILED: true, TRANSPARENT: false, VERSION: '1.1.1', SRS: MAP_PROJECTION_CODE },
    serverType: 'geoserver',
    crossOrigin: 'anonymous',
    tileGrid: geomolgTileGrid,
  });
  return new TileLayer({
    source: src,
    visible: false,
    useInterimTilesOnError: true,
    properties: { isBase: true },
    transition: 0,
  });
}
async function bmWMTSFromCaps({ capsUrl, layer, matrixSet, format = 'image/jpeg', style = 'default' }) {
  const xml = await (await fetch(capsUrl, { mode: 'cors' })).text();
  const caps = new WMTSCapabilities().read(xml);
  const tryList = Array.isArray(matrixSet) ? matrixSet : [matrixSet];
  let opts = null;
  for (const m of tryList) {
    const o = optionsFromCapabilities(caps, { layer, matrixSet: m, format, style });
    if (o) { opts = o; break; }
  }
  if (!opts) throw new Error('WMTS options not found');
  return new TileLayer({ source: new WMTS({ ...opts, crossOrigin: 'anonymous' }), visible: false, properties: { isBase: true } });
}
function lazyLayer(promiseFactory, fallbackFactory) {
  const placeholder = new TileLayer({ visible: false, properties: { isBase: true } });
  promiseFactory.then((real) => placeholder.setSource(real.getSource()))
    .catch(() => fallbackFactory && placeholder.setSource(fallbackFactory().getSource()));
  return placeholder;
}

/* =============== Local thumbnails =============== */
const openStreetThumb = new URL('../../assets/imgbasemap/OpenStreet.png', import.meta.url).href;
const world_satThumb = new URL('../../assets/imgbasemap/world_sat.png', import.meta.url).href;
const ortho2023Thumb = new URL('../../assets/imgbasemap/2023.png', import.meta.url).href;
const ortho2021Thumb = new URL('../../assets/imgbasemap/2021.png', import.meta.url).href;
const ortho2000Thumb = new URL('../../assets/imgbasemap/2000.png', import.meta.url).href;
const ortho1970Thumb = new URL('../../assets/imgbasemap/1970.png', import.meta.url).href;
const orthoSwipeThumb = new URL('../../assets/imgbasemap/2023-1970.png', import.meta.url).href;

/* =============== Imagery groups (years) =============== */

// Current Imagery List being used (Defaults to WB)
let CURRENT_IMAGERY_LIST = IMAGERY_WB;
let cachedImagerySet = new Set(IMAGERY_WB.map(x => x.id));

/* =============== Basemap layer definitions =============== */
// Re-used 1970 GeoServer source for swipe mode.
const URL_1970_WMS = 'https://geopst.com/geoserver/DataBase/wms';
const LAYER_1970_NAME = 'DataBase:ImageryWB1970';

// Gaza factories use standard TileArcGISRest because they might not be tiled nicely for XYZ or just to be safe
const gazaLayers = IMAGERY_GAZA.map(img => ({
  id: img.id,
  title: `Gaza ${img.year}`,
  thumb: ortho2023Thumb, // Placeholder
  factory: () => bmArcGISTiles(GAZA_IMAGERY_SERVICES[String(img.year).replace('.', '')], REGION_EXTENTS.GAZA) // Pass Gaza extent
}));

const BASEMAPS = [
  {
    id: 'streets',
    title: 'Streets (OSM)',
    thumb: openStreetThumb,
    factory: () => bmXYZ('https://tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19 }),
  },

  // 2024 - newest West Bank imagery
  { id: 'ortho2024', title: 'Aerial 2024', thumb: ortho2023Thumb, factory: () => bmArcGISTiles('https://orthophotos.geomolg.ps/adaptor/rest/services/Orthophotos_WB_2024_15cm_tif_PG1923/MapServer') },
  {
    id: BACKUP_IMAGERY_ID,
    title: 'Aerial Backup 2023',
    thumb: ortho2023Thumb,
    factory: () => bmArcGIS(BACKUP_WB_2023_URL)
  },
  // 2023 (Turbo mode)
  {
    id: 'ortho2023',
    title: 'Aerial 2023 (Turbo)',
    thumb: ortho2023Thumb,
    factory: () => bmArcGISTiles(ARCGIS_SERVICES.IMAGERY_2023),
  },
  // ... Include all WB layers (Simplified via loop to save space if this was dry, but list is explicit)
  { id: 'ortho2022', title: 'Aerial 2022', thumb: ortho2021Thumb, factory: () => bmArcGISTiles('https://orthophotos.geomolg.ps/adaptor/rest/services/Orthophotos_WB_2022_15cm_tif_PG1923_05/MapServer') },
  { id: 'ortho2021', title: 'Aerial 2021', thumb: ortho2021Thumb, factory: () => bmArcGISTiles('https://orthophotos.geomolg.ps/adaptor/rest/services/Orthophotos_WB_2021_15cm_tif_PG1923/MapServer') },
  { id: 'ortho2020', title: 'Aerial 2020', thumb: ortho2021Thumb, factory: () => bmArcGISTiles('https://orthophotos.geomolg.ps/adaptor/rest/services/Orthophotos_WB_2020_25cm_jpg_PG1923/MapServer') },
  { id: 'ortho2019', title: 'Aerial 2019', thumb: ortho2021Thumb, factory: () => bmArcGISTiles('https://orthophotos.geomolg.ps/adaptor/rest/services/Orthophotos_WB_2019_Aerial_25cm_jpg_PG1923/MapServer') },
  { id: 'ortho2018', title: 'Aerial 2018', thumb: ortho2021Thumb, factory: () => bmArcGISTiles('https://orthophotos.geomolg.ps/adaptor/rest/services/Orthophotos_WB_2018_Aerial_10cm_jpg_PG1923/MapServer') },
  { id: 'ortho2016', title: 'Aerial 2016', thumb: ortho2021Thumb, factory: () => bmArcGISTiles('https://orthophotos.geomolg.ps/adaptor/rest/services/Orthophotos_WB_2016_Aerial_10cm_jpg_PG1923/MapServer') },
  { id: 'ortho2015', title: 'Aerial 2015', thumb: ortho2021Thumb, factory: () => bmArcGISTiles('https://orthophotos.geomolg.ps/adaptor/rest/services/Orthophotos_WB_2015_Aerial_25cm_jpg_PG1923/MapServer') },
  { id: 'ortho2014', title: 'Aerial 2014', thumb: ortho2021Thumb, factory: () => bmArcGISTiles('https://orthophotos.geomolg.ps/adaptor/rest/services/Orthophotos_WB_2014_Aerial_25cm_jpg_PG1923/MapServer') },
  { id: 'ortho2013', title: 'Aerial 2013', thumb: ortho2021Thumb, factory: () => bmArcGISTiles('https://orthophotos.geomolg.ps/adaptor/rest/services/Orthophotos_WB_2013_Aerial_50cm_jpg_PG1923/MapServer') },
  { id: 'ortho2012', title: 'Aerial 2012', thumb: ortho2021Thumb, factory: () => bmArcGISTiles('https://orthophotos.geomolg.ps/adaptor/rest/services/Orthophotos_WB_2012_Aerial_50cm_jpg_PG1923/MapServer') },
  { id: 'ortho2011', title: 'Aerial 2011', thumb: ortho2021Thumb, factory: () => bmArcGISTiles('https://orthophotos.geomolg.ps/adaptor/rest/services/Orthophotos_WB_2011_Aerial_50cm_jpg_PG1923/MapServer') },
  { id: 'ortho2010', title: 'Aerial 2010', thumb: ortho2021Thumb, factory: () => bmArcGISTiles('https://orthophotos.geomolg.ps/adaptor/rest/services/Orthophotos_WB_2010_Aerial_50cm_jpg_PG1923/MapServer') },
  { id: 'ortho2009', title: 'Aerial 2009', thumb: ortho2021Thumb, factory: () => bmArcGISTiles('https://orthophotos.geomolg.ps/adaptor/rest/services/Orthophotos_WB_2009_Aerial_50cm_jpg_PG1923/MapServer') },
  { id: 'ortho2008', title: 'Aerial 2008', thumb: ortho2021Thumb, factory: () => bmArcGISTiles('https://orthophotos.geomolg.ps/adaptor/rest/services/Orthophotos_WB_2008_Aerial_50cm_jpg_PG1923/MapServer') },
  { id: 'ortho2006', title: 'Aerial 2006', thumb: ortho2021Thumb, factory: () => bmArcGISTiles('https://orthophotos.geomolg.ps/adaptor/rest/services/Orthophotos_WB_2006_Aerial_50cm_jpg_PG1923/MapServer') },
  { id: 'ortho2005', title: 'Aerial 2005', thumb: ortho2021Thumb, factory: () => bmArcGISTiles('https://orthophotos.geomolg.ps/adaptor/rest/services/Orthophotos_WB_2005_Aerial_75cm_jpg_PG1923/MapServer') },
  { id: 'ortho2004', title: 'Aerial 2004', thumb: ortho2021Thumb, factory: () => bmArcGISTiles('https://orthophotos.geomolg.ps/adaptor/rest/services/Orthophotos_WB_2004_Aerial_100cm_jpg_PG1923/MapServer') },
  { id: 'ortho2003', title: 'Aerial 2003', thumb: ortho2021Thumb, factory: () => bmArcGISTiles('https://orthophotos.geomolg.ps/adaptor/rest/services/Orthophotos_WB_2003_Aerial_100cm_jpg_PG1923/MapServer') },
  { id: 'ortho2002', title: 'Aerial 2002', thumb: ortho2021Thumb, factory: () => bmArcGISTiles('https://orthophotos.geomolg.ps/adaptor/rest/services/Orthophotos_WB_2002_Aerial_80cm_jpg_PG1923/MapServer') },
  { id: 'ortho2001', title: 'Aerial 2001', thumb: ortho2000Thumb, factory: () => bmArcGISTiles('https://orthophotos.geomolg.ps/adaptor/rest/services/Orthophotos_WB_2001_Aerial_100cm_jpg_PG1923/MapServer') },
  { id: 'ortho2000', title: 'Aerial 2000', thumb: ortho2000Thumb, factory: () => bmArcGISTiles('https://orthophotos.geomolg.ps/adaptor/rest/services/Orthophotos_WB_2000_Aerial_80cm_jpg_PG1923/MapServer') },
  { id: 'ortho1999', title: 'Aerial 1999', thumb: ortho2021Thumb, factory: () => bmArcGISTiles('https://orthophotos.geomolg.ps/adaptor/rest/services/Orthophotos_WB_1999_Aerial_125cm_jpg_PG1923/MapServer') },
  { id: 'ortho1997', title: 'Aerial 1997', thumb: ortho2021Thumb, factory: () => bmArcGISTiles('https://orthophotos.geomolg.ps/adaptor/rest/services/Orthophotos_WB_1997_Aerial_125cm_jpg_PG1923/MapServer') },
  {
    id: 'ortho_geoserver_1970',
    title: 'Aerial 1970 (GeoServer)',
    thumb: ortho1970Thumb,
    factory: () => bmTileWMS({ url: URL_1970_WMS, layers: LAYER_1970_NAME, format: 'image/jpeg' }),
  },

  // ADD GAZA LAYERS
  ...gazaLayers,

  // SWIPE
  {
    id: 'swipe_compare',
    title: 'Swipe Compare',
    thumb: orthoSwipeThumb,
    factory: () => createSwipeGroup('ortho_geoserver_1970', 'ortho2024'),
  },

  // SATELLITE
  {
    id: 'world_sat',
    title: 'World Imagery (ESRI)',
    thumb: world_satThumb,
    factory: () => bmXYZ('https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', { maxZoom: 19 }),
  },

  {
    id: 'google_hybrid',
    title: 'Google Hybrid',
    thumb: world_satThumb,
    factory: () => bmXYZ('https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}', { maxZoom: 21 }),
  },
];

/* =============== Swipe helpers =============== */
let swipeKeys = [];
function detachSwipe() {
  if (swipeKeys.length) {
    swipeKeys.forEach(unByKey);
    swipeKeys = [];
  }
}
function attachSwipeToLayer(topLayer, getRatioFn) {
  detachSwipe();
  if (!topLayer || typeof getRatioFn !== 'function') return;

  const preKey = topLayer.on('prerender', (event) => {
    const ctx = event.context;
    const pixelRatio = event.frameState.pixelRatio || 1;
    const size = map.getSize();
    const ratio = getRatioFn();
    const clipWidthPx = (size ? size[0] : ctx.canvas.width / pixelRatio) * ratio * pixelRatio;

    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, clipWidthPx, ctx.canvas.height);
    ctx.clip();
  });

  const postKey = topLayer.on('postrender', (event) => {
    const ctx = event.context;
    const pixelRatio = event.frameState.pixelRatio || 1;
    const size = map.getSize();
    const ratio = getRatioFn();
    const clipWidthPx = (size ? size[0] : ctx.canvas.width / pixelRatio) * ratio * pixelRatio;

    ctx.restore();

    // Draw handle and line AFTER restore so they aren't clipped
    ctx.beginPath();
    ctx.strokeStyle = 'rgba(220, 53, 69, 0.8)';
    ctx.lineWidth = 4 * pixelRatio;
    ctx.moveTo(clipWidthPx, 0);
    ctx.lineTo(clipWidthPx, ctx.canvas.height);
    ctx.stroke();

    ctx.beginPath();
    ctx.arc(clipWidthPx, ctx.canvas.height / 2, 20 * pixelRatio, 0, 2 * Math.PI);
    ctx.fillStyle = 'rgba(220, 53, 69, 0.8)';
    ctx.fill();
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 2 * pixelRatio;
    ctx.stroke();
  });

  swipeKeys = [preKey, postKey];
}

function createSwipeGroup(topId, bottomId) {
  const getFactory = (id) => BASEMAPS.find(b => b.id === id)?.factory;
  const topFactory = getFactory(topId);
  const bottomFactory = getFactory(bottomId);

  if (!topFactory || !bottomFactory) {
    console.error("Layer factory not found for swipe", topId, bottomId);
    return new Group({});
  }

  const topLayer = topFactory();
  const bottomLayer = bottomFactory();
  topLayer.setVisible(true);
  bottomLayer.setVisible(true);

  return new Group({
    layers: [bottomLayer, topLayer],
    properties: {
      isBase: true,
      swipeTop: topLayer,
      swipeBottom: bottomLayer,
      topId: topId,
      bottomId: bottomId
    },
    visible: false,
  });
}

/* =============== UI initialization =============== */
const DEFAULT_BASE = 'world_sat'; // Start with Satellite as per request (zoomed out)

// Build all layers once
const baseLayers = BASEMAPS.map((bm) => {
  const isDefault = bm.id === DEFAULT_BASE;
  const mustBeConcreteLayer = bm.id === 'swipe_compare';

  if (isDefault || mustBeConcreteLayer) {
    const layer = bm.factory();
    layer.set('id', bm.id);
    layer.set('title', bm.title);
    layer.set('isBase', true);
    layer.set('isLazy', false);
    return layer;
  } else {
    // Performance: skeleton layer; real source is injected on first activation in setBaseMap.
    return new TileLayer({
      visible: false,
      properties: {
        id: bm.id,
        title: bm.title,
        isBase: true,
        isLazy: true,
        bmFactory: bm.factory
      }
    });
  }
});

const baseGroup = new Group({ layers: baseLayers, properties: { isBaseGroup: true } });
map.getLayers().insertAt(0, baseGroup);

// Helpers for lists (Moved up)

const BASEMAP_ALIASES = {
  'imagery2023': 'ortho2024',
  'aerial': 'ortho2024'
};

let imageryYearsMode = false;

function getCurrentBaseId() {
  return cachedBaseId;
}

// NOTE: indexOfImageryById must now search CURRENT_IMAGERY_LIST
function indexOfImageryById(id) {
  return CURRENT_IMAGERY_LIST.findIndex(x => x.id === id);
}

/* =============== UI References =============== */
const pop = document.getElementById('bm-popover');
const grid = pop?.querySelector('.bm-grid');
const toggleListBtn = document.getElementById('basemap-toggle');
const closeListBtn = pop?.querySelector('.bm-close');

const yearWidget = document.getElementById('imagery-year-widget');
const yearToggle = document.getElementById('imagery-year-toggle');
const yearLabel = document.getElementById('imagery-year-label');
const yearRange = document.getElementById('imagery-year-range');
const yearPrev = document.getElementById('img-prev');
const yearNext = document.getElementById('img-next');
const yearClose = document.getElementById('img-close');
const firstYearEl = document.getElementById('iw-first-year');
const lastYearEl = document.getElementById('iw-last-year');

const swipeWidget = document.getElementById('swipe-widget');
const swipeRange = document.getElementById('swipe-range');
const swipeClose = document.getElementById('swipe-close');
const swipeLeftSelect = document.getElementById('swipe-left-select');
const swipeRightSelect = document.getElementById('swipe-right-select');

/* =============== Widget Logic =============== */

function updateWidgetRange() {
  if (yearRange && firstYearEl && lastYearEl && CURRENT_IMAGERY_LIST.length > 0) {

    const effectiveList = CURRENT_IMAGERY_LIST;

    yearRange.min = 0;
    yearRange.max = String(effectiveList.length - 1);

    if (effectiveList.length > 0) {
      firstYearEl.textContent = String(effectiveList[0].year);
      lastYearEl.textContent = String(effectiveList[effectiveList.length - 1].year);
    }

    // Adjust value to match current layer in the filtered list
    const currentId = getCurrentBaseId();
    const idx = effectiveList.findIndex(x => x.id === currentId);

    if (idx !== -1) {
      yearRange.value = idx;
    }
  }
}

function populateSwipeSelects() {
  if (!swipeLeftSelect || !swipeRightSelect) return;
  const oldLeft = swipeLeftSelect.value;
  const oldRight = swipeRightSelect.value;
  swipeLeftSelect.innerHTML = '';
  swipeRightSelect.innerHTML = '';

  const effectiveList = CURRENT_IMAGERY_LIST;

  effectiveList.forEach(img => {
    const optL = document.createElement('option');
    optL.value = img.id;
    optL.textContent = img.year;
    swipeLeftSelect.appendChild(optL);

    const optR = document.createElement('option');
    optR.value = img.id;
    optR.textContent = img.year;
    swipeRightSelect.appendChild(optR);
  });

  // Try to preserve selection if exists in new list, else defaults
  if (effectiveList.length === 0) return; // Guard: empty list after fallback
  if (effectiveList.find(x => x.id === oldLeft)) swipeLeftSelect.value = oldLeft;
  else swipeLeftSelect.value = effectiveList[effectiveList.length - 1].id; // Oldest

  if (effectiveList.find(x => x.id === oldRight)) swipeRightSelect.value = oldRight;
  else swipeRightSelect.value = effectiveList[0].id; // Newest
}

function updateSwipeLayers() {
  const leftId = swipeLeftSelect?.value;
  const rightId = swipeRightSelect?.value;
  if (!leftId || !rightId || cachedBaseId !== 'swipe_compare') return;

  const swipeGroupLayer = baseGroup.getLayers().getArray().find(l => l.get('id') === 'swipe_compare');
  if (!swipeGroupLayer) return;

  // Swipe Memory Leak: Cleanup old layer monitoring before replacement
  const oldCleanup = swipeGroupLayer.get('_cleanup');
  if (oldCleanup) {
    oldCleanup();
    swipeGroupLayer.set('_cleanup', null);
  }

  const getFactory = (id) => BASEMAPS.find(b => b.id === id)?.factory;
  const topFactory = getFactory(leftId);
  const bottomFactory = getFactory(rightId);

  if (topFactory && bottomFactory) {
    const newTop = topFactory();
    const newBottom = bottomFactory();
    newTop.setVisible(true);
    newBottom.setVisible(true);

    swipeGroupLayer.setLayers(new Collection([newBottom, newTop]));
    swipeGroupLayer.set('swipeTop', newTop);
    swipeGroupLayer.set('swipeBottom', newBottom);

    // High-Perf Cleanup for Swipe
    newTop.set('_cleanup', newTop.get('_cleanup') || (() => { }));
    newBottom.set('_cleanup', newBottom.get('_cleanup') || (() => { }));

    swipeGroupLayer.set('_cleanup', () => {
      [newTop, newBottom].forEach(l => {
        const c = l.get('_cleanup'); if (c) c();
        const s = l.getSource(); if (s && s.clear) s.clear();
      });
    });

    bindSwipeIfActive();
  }
}

function isAnyImageryActive() {
  return cachedImagerySet.has(cachedBaseId);
}

function updateImageryWidgetVisibility() {
  if (isRestricted) {
    if (yearWidget) yearWidget.classList.add('hidden');
    if (yearToggle) yearToggle.style.opacity = '.35';
    return;
  }
  const show = imageryYearsMode && isAnyImageryActive() && !isYearWidgetManuallyClosed;

  if (show !== isYearWidgetVisible) {
    if (yearWidget) yearWidget.classList.toggle('hidden', !show);
    if (yearToggle) yearToggle.style.opacity = show ? '1' : '.35';
    isYearWidgetVisible = show;
  }
}

function syncImageryWidgetToActive() {
  const id = getCurrentBaseId();

  const effectiveList = CURRENT_IMAGERY_LIST;

  const idx = effectiveList.findIndex(x => x.id === id);
  if (!yearRange || !yearLabel || idx === -1) return;

  const imgObj = effectiveList[idx];
  const span = yearLabel.querySelector('span') || yearLabel;
  const newText = String(imgObj.year);

  if (span.textContent !== newText) {
    span.textContent = newText;
    span.classList.remove('year-pop');
    void span.offsetWidth;
    span.classList.add('year-pop');
  }
  yearRange.value = String(idx);
}

function nextImagery(delta) {
  const currentId = getCurrentBaseId();
  const effectiveList = CURRENT_IMAGERY_LIST;

  let idx = effectiveList.findIndex(x => x.id === currentId);
  if (idx < 0) idx = 0; else idx = (idx + delta + effectiveList.length) % effectiveList.length;
  setBaseMap(effectiveList[idx].id);
}

function updateSwipeWidgetVisibility() {
  if (isRestricted) {
    swipeWidget?.classList.add('hidden');
    return;
  }
  const show = getCurrentBaseId() === 'swipe_compare' && !isSwipeWidgetManuallyClosed;

  if (show !== isSwipeWidgetVisible) {
    swipeWidget?.classList.toggle('hidden', !show);
    isSwipeWidgetVisible = show;
  }
}

function bindSwipeIfActive() {
  if (getCurrentBaseId() !== 'swipe_compare') {
    detachSwipe();
    return;
  }
  const layer = baseGroup.getLayers().getArray().find(l => l.get('id') === 'swipe_compare');
  if (layer && layer instanceof Group) {
    const top = layer.get('swipeTop') || layer.getLayers().item(1);
    const getRatio = () => {
      const v = swipeRange ? Number(swipeRange.value) : 50;
      return Math.min(1, Math.max(0, v / 100));
    };
    attachSwipeToLayer(top, getRatio);
  }
}



function activateGeomolgFallback() {
  if (isGeomolgOffline) return;
  isGeomolgOffline = true;
  geomolgConsecutiveFailures = 0;

  const currentId = getCurrentBaseId();
  const isGaza = containsCoordinate(REGION_EXTENTS.GAZA, map.getView().getCenter());
  const fallbackBaseId = isGaza ? GOOGLE_HYBRID_ID : (BASEMAPS.some(b => b.id === BACKUP_IMAGERY_ID) ? BACKUP_IMAGERY_ID : 'world_sat');

  const toRemove = [];
  baseGroup.getLayers().forEach(l => {
    const id = l.get('id');
    const isImagery = imageryIdsWB.has(id) || imageryIdsGaza.has(id);
    if (isImagery && id !== 'ortho_geoserver_1970' && id !== BACKUP_IMAGERY_ID) {
      toRemove.push(id);
    }
  });

  if (toRemove.length === 0) {
    if (cachedBaseId !== 'world_sat' && cachedBaseId !== BACKUP_IMAGERY_ID && cachedBaseId !== GOOGLE_HYBRID_ID && cachedBaseId !== 'streets') {
      setBaseMap(fallbackBaseId, true);
    }
    return;
  }

  toRemove.forEach(id => {
    const layer = baseGroup.getLayers().getArray().find(l => l.get('id') === id);
    if (layer) baseGroup.getLayers().remove(layer);

    const wbIdx = IMAGERY_WB.findIndex(x => x.id === id);
    if (wbIdx !== -1) IMAGERY_WB.splice(wbIdx, 1);

    const gazaIdx = IMAGERY_GAZA.findIndex(x => x.id === id);
    if (gazaIdx !== -1) IMAGERY_GAZA.splice(gazaIdx, 1);
  });

  imageryIdsWB.clear();
  IMAGERY_WB.forEach(x => imageryIdsWB.add(x.id));
  imageryIdsGaza.clear();
  IMAGERY_GAZA.forEach(x => imageryIdsGaza.add(x.id));

  Object.keys(BASEMAP_ALIASES).forEach(key => {
    if (toRemove.includes(BASEMAP_ALIASES[key])) {
      BASEMAP_ALIASES[key] = fallbackBaseId;
    }
  });

  if (toRemove.includes(currentId)) {
    console.log(`[BasemapSwitcher] Geomolg tiles failed. Falling back to '${fallbackBaseId}'.`);
    setBaseMap(fallbackBaseId, true);
  }

  CURRENT_IMAGERY_LIST = cachedRegion === 'Gaza' ? IMAGERY_GAZA : IMAGERY_WB;
  updateWidgetRange();
  cachedImagerySet = new Set(CURRENT_IMAGERY_LIST.map(x => x.id));
  populateSwipeSelects();

  if (!window._geomolgNotified) {
    const msg = isGaza
      ? 'الصور الجوية (غزة) غير متوفرة حالياً، تم التحويل تلقائياً لصور Google Maps.'
      : 'الصور الجوية لا تعمل في الوقت الحالي، سيتم التحويل إلى الصور الجوية الاحتياطية.';
    if (window.showErrorModal) window.showErrorModal(msg);
    else alert(msg);
    window._geomolgNotified = true;
  }
}

(function init() {
  map.on('moveend', checkMapState);

  grid?.querySelectorAll('.bm-card').forEach(btn => {
    btn.addEventListener('click', () => {
      let id = btn.getAttribute('data-basemap');

      if (id === 'imagery-group') {
        if (isRestricted) {
          if (window.showErrorModal) window.showErrorModal('Zoom in past 1:25000 to use yearly aerial layers.');
          else alert('Zoom in past 1:25000 to use yearly aerial layers.');
          pop.classList.add('hidden');
          return;
        }
        imageryYearsMode = true;
        setBaseMap(CURRENT_IMAGERY_LIST[0].id);
        pop.classList.add('hidden');
        return;
      }

      if (id === 'swipe1970_2023') id = 'swipe_compare';
      if (id in BASEMAP_ALIASES) id = BASEMAP_ALIASES[id];

      imageryYearsMode = false;
      setBaseMap(id);
      yearWidget?.classList.add('hidden');
      pop.classList.add('hidden');
    });
  });

  toggleListBtn?.addEventListener('click', (e) => {
    e.stopPropagation();
    pop.classList.toggle('hidden');

    // Smart NotePin: Close if open
    if (!pop.classList.contains('hidden')) {
      if (window.featureSheet && window.featureSheet.isVisible() &&
        window.featureSheet.data && window.featureSheet.data.isPin) {
        window.featureSheet.close();
      }
    }
  });

  closeListBtn?.addEventListener('click', (e) => {
    e.stopPropagation();
    pop.classList.add('hidden');
  });

  document.addEventListener('click', (e) => {
    const within = document.getElementById('basemap-switcher')?.contains(e.target);
    if (!within) pop?.classList.add('hidden');
  });

  yearPrev?.addEventListener('click', () => nextImagery(-1));
  yearNext?.addEventListener('click', () => nextImagery(+1));
  yearClose?.addEventListener('click', () => {
    isYearWidgetManuallyClosed = true;
    updateImageryWidgetVisibility();
  });

  yearRange?.addEventListener('input', () => {
    const idx = Number(yearRange.value);
    const effectiveList = CURRENT_IMAGERY_LIST;

    if (effectiveList[idx]) {
      setBaseMap(effectiveList[idx].id);
    }
  });

  yearToggle?.addEventListener('click', (e) => {
    e.stopPropagation();
    if (imageryYearsMode && isAnyImageryActive()) {
      // Toggle the manual close state to flip visibility
      isYearWidgetManuallyClosed = !isYearWidgetManuallyClosed;
      updateImageryWidgetVisibility();
      syncImageryWidgetToActive();
    } else {
      yearToggle?.animate(
        [{ transform: 'scale(1)' }, { transform: 'scale(1.15)' }, { transform: 'scale(1)' }],
        { duration: 300 }
      );
    }
  });

  swipeRange?.addEventListener('input', () => {
    if (getCurrentBaseId() === 'swipe_compare') map.render();
  });

  swipeClose?.addEventListener('click', () => {
    isSwipeWidgetManuallyClosed = true;
    updateSwipeWidgetVisibility();
  });

  swipeLeftSelect?.addEventListener('change', updateSwipeLayers);
  swipeRightSelect?.addEventListener('change', updateSwipeLayers);

  // High-Perf Priming
  const center = map.getView().getCenter();
  cachedRegion = containsCoordinate(REGION_EXTENTS.GAZA, center) ? 'Gaza' : 'WB';
  CURRENT_IMAGERY_LIST = cachedRegion === 'Gaza' ? IMAGERY_GAZA : IMAGERY_WB;
  cachedImagerySet = new Set(CURRENT_IMAGERY_LIST.map(x => x.id));

  // DOM Caching for Performance
  bmPopover = document.getElementById('bm-popover');
  btn2023 = bmPopover?.querySelector('button[data-basemap="imagery2023"]');
  btnYears = bmPopover?.querySelector('button[data-basemap="imagery-group"] span');
  btnSwipe = bmPopover?.querySelector('button[data-basemap="swipe1970_2023"] span');

  updateWidgetRange();
  populateSwipeSelects();

  const view = map.getView();
  const startRes = view.getResolution();

  // ? Intelligent Startup: Choose basemap based on initial zoom
  if (startRes > RESTRICT_RES_THRESHOLD) {
    isRestricted = true;
    setBaseMap('world_sat');
  } else {
    isRestricted = false;
    // Check if the user has a saved preference, otherwise default to latest Aerial
    const saved = localStorage.getItem('geopst_basemap');
    if (saved && saved !== 'world_sat' && BASEMAPS.some(b => b.id === saved)) {
      setBaseMap(saved);
    } else {
      // Force Aerial if we are zoomed in on boot (typical for shared links)
      setBaseMap(CURRENT_IMAGERY_LIST[0].id);
      imageryYearsMode = true; // Show the year widget if we started zoomed in
    }
  }

  // Probe was moved to the top of init() for immediate protection
})();

window.setBaseMap = setBaseMap;

/**
 * Updates the opacity of the entire basemap group.
 * @param {number} opacity - 0 to 1
 */
export function setBasemapOpacity(opacity) {
  if (typeof baseGroup !== 'undefined' && baseGroup) {
    baseGroup.setOpacity(opacity);
  }
}

export { baseGroup, setBaseMap }; 
